import ModalController from "./modal_controller"
import Rails from "@rails/ujs"

export default class extends ModalController {
  static targets = [...ModalController.targets, "input", "typeRadio", "loaderImg", "errorExplanation", "codeHint"]
  static values = { locale: String, endpointUrl: String, placeholder: String, prefix: String }

  get selectedType() {
    return this.typeRadioTargets.find((element) => element.checked)?.value
  }

  connect() {
    super.connect()
    this.inputTarget.value = ""
  }

  open() {
    super.open()
    this.inputTarget.focus()
  }

  changeType(event) {
    this.inputTarget.placeholder = `${this.placeholderValue} ${event.params.label}`
  }

  submit() {
    this.loaderImgTarget.classList.remove('hidden')
    this.setErrors([])

    const params = { type: this.selectedType }
    // OSTROŻNIE: czasem drugi parametr to "number", czasem "numer"
    let handleResults
    switch (this.localeValue) {
      case "pl":
        params.numer = this.inputTarget.value
        handleResults = this.handleResultsPl
        break
      case "uk":
        params.number = this.inputTarget.value
        handleResults = this.handleResultsUk
        break
      case "cz":
        params.number = this.inputTarget.value
        handleResults = this.handleResultsCz
        break
      case "sk":
        params.number = this.inputTarget.value
        handleResults = this.handleResultsSk
        break
      case "fr":
        params.numer = this.inputTarget.value
        handleResults = this.handleResultsFr
        break
      case "ua": {
        params.number = this.inputTarget.value
        handleResults = this.handleResultsUa
        const info = this.detectUaCodeType(params.number)
        if (!info.valid) {
          this.loaderImgTarget.classList.add('hidden')
          const msg = info.type ? `${info.label} — невірна контрольна сума` : 'ЄДРПОУ = 8 цифр, РНОКПП = 10 цифр'
          this.setErrors([msg])
          return
        }
        break
      }
      default:
        throw new Error(`unknown locale: ${this.localeValue}`)
    }
    handleResults = handleResults.bind(this)

    Rails.ajax({
      type: 'post',
      url: this.endpointUrlValue,
      dataType: 'json',
      data: new URLSearchParams(params),
      success: (data) => {
        this.loaderImgTarget.classList.add("hidden")
        if (!(Array.isArray(data.errors) && data.errors.length > 0)) {
          handleResults(data)
          this.hide()
          window.dispatchEvent(new CustomEvent("update-form"))
        } else {
          this.setErrors(data.errors)
          this.enter()
          this.containerTarget.classList.remove("hidden")
        }
      },
    })
  }

  hide() {
    this.inputTarget.value = ""
    this.leave()
  }

  /** @param {string[]} errors */
  setErrors(errors) {
    const errorElements = errors.map((error) => {
      const element = document.createElement("p")
      element.innerText = error
      return element
    })
    this.errorExplanationTarget.replaceChildren(...errorElements)
    this.errorExplanationTarget.classList.toggle("hidden", errors.length === 0)
    this.inputTarget.parentNode.classList.toggle("field_with_errors", errors.length > 0)
  }

  /**
   * wrapper na ustawianie wartości elementom podobnie do jquery
   * czyli bez rzucania błędem jeśli nie zostanie znaleziony (bo np nie ma go w formatce)
   */
  trySetValue(id, value) {
    const element = document.getElementById(id)
    if (element !== null) {
      element.value = value
    }
  }

  handleResultsPl({ results }) {
    this.trySetValue(`${this.prefixValue}_name`, results.name)
    this.trySetValue(`${this.prefixValue}_tax_no`, results.nip)
    this.trySetValue(`${this.prefixValue}_street`, results.street)
    this.trySetValue(`${this.prefixValue}_post_code`, results.post_code)
    this.trySetValue(`${this.prefixValue}_city`, results.city)
    this.trySetValue(`${this.prefixValue}_country`, "PL")
    this.trySetValue(`${this.prefixValue}_email`, results.email)
    this.trySetValue(`${this.prefixValue}_fax`, results.fax)
    this.trySetValue(`${this.prefixValue}_phone`, results.phone)

    if (this.prefixValue === "client") {
      this.trySetValue(`${this.prefixValue}_register_number`, results.regon)
    }
  }

  handleResultsUk({ results }) {
    this.trySetValue(`${this.prefixValue}_name`, results.company_name)
    if (this.prefixValue === "department") {
      this.trySetValue(`${this.prefixValue}_kind`, results.company_status)
    } else {
      this.trySetValue(`${this.prefixValue}_shortcut`, results.company_name)
    }
    this.trySetValue(`${this.prefixValue}_street`, `${results.registered_office_address.address_line_1} ${results.registered_office_address.address_line_2}`)
    this.trySetValue(`${this.prefixValue}_post_code`, results.registered_office_address.postal_code)
    this.trySetValue(`${this.prefixValue}_city`, results.registered_office_address.locality)
  }

  handleResultsCz({ results, dic }) {
    this.trySetValue(`${this.prefixValue}_name`, results.obchodniJmeno)
    this.trySetValue(`${this.prefixValue}_register_number`, results.ico)
    this.trySetValue(`${this.prefixValue}_tax_no`, dic)
    this.trySetValue(`${this.prefixValue}_street`, results.sidlo.textovaAdresa.split(",")[0])
    this.trySetValue(`${this.prefixValue}_post_code`, results.sidlo.psc)
    this.trySetValue(`${this.prefixValue}_city`, results.sidlo.nazevObce)
    this.trySetValue(`${this.prefixValue}_country`, results.sidlo.kodStatu)
  }

  handleResultsSk({ results }) {
    this.trySetValue(`${this.prefixValue}_name`, results.name)
    this.trySetValue(`${this.prefixValue}_tax_no`, results.taxno)
    this.trySetValue(`${this.prefixValue}_street`, `${results.address.street} ${results.address.number}`)
    this.trySetValue(`${this.prefixValue}_post_code`, results.address.postal_code)
    this.trySetValue(`${this.prefixValue}_city`, results.address.municipality)
    this.trySetValue(`${this.prefixValue}_register_number`, results.regno)
    this.trySetValue(`${this.prefixValue}_country`, results.address.country_code.toUpperCase())
  }

  handleResultsFr({ results }) {
    this.trySetValue(`${this.prefixValue}_name`, results.name)
    this.trySetValue(`${this.prefixValue}_tax_no`, results.tax_no)
    this.trySetValue(`${this.prefixValue}_street`, results.address)
    this.trySetValue(`${this.prefixValue}_post_code`, results.postal_code)
    this.trySetValue(`${this.prefixValue}_city`, results.city)
    this.trySetValue(`${this.prefixValue}_country`, results.country)
    this.trySetValue(`${this.prefixValue}_tax_no_kind`, results.kind === "siren" ? "SIREN" : results.kind === "no_enterprise" ? "N° entreprise" : "")
    document.getElementById(`${this.prefixValue}_tax_no`)?.dispatchEvent(new CustomEvent("change", { bubbles: true, detail: { source: "autocomplete" } }))
  }

  // --- walidacja ukraińskich kodów ---

  validateEdrpou(code) {
    const d = code.split('').map(Number)
    let w = [3, 4, 5].includes(d[0]) ? [7, 1, 2, 3, 4, 5, 6] : [1, 2, 3, 4, 5, 6, 7]
    let sum = w.reduce((s, wi, i) => s + d[i] * wi, 0)
    let check = sum % 11
    if (check >= 10) {
      w = w.map(x => x + 2)
      sum = w.reduce((s, wi, i) => s + d[i] * wi, 0)
      check = (sum % 11) % 10
    }
    return check === d[7]
  }

  validateRnokpp(code) {
    const d = code.split('').map(Number)
    const w = [-1, 5, 7, 9, 4, 6, 10, 5, 7]
    const sum = w.reduce((s, wi, i) => s + d[i] * wi, 0)
    return ((sum % 11) % 10) === d[9]
  }

  detectUaCodeType(number) {
    const digits = number.replace(/\D/g, '')
    if (digits.length === 8) return { type: 'edrpou', label: 'ЄДРПОУ', valid: this.validateEdrpou(digits) }
    if (digits.length === 10) return { type: 'rnokpp', label: 'РНОКПП/ІПН', valid: this.validateRnokpp(digits) }
    return { type: null, label: null, valid: false }
  }

  updateCodeHint() {
    if (!this.hasCodeHintTarget) return
    const digits = this.inputTarget.value.replace(/\D/g, '')
    const hint = this.codeHintTarget

    if (digits.length === 0) {
      hint.textContent = ''
      hint.className = 'text-sm sm:text-xs h-4'
      return
    }

    const info = this.detectUaCodeType(this.inputTarget.value)
    if (info.type) {
      if (info.valid) {
        hint.textContent = `${info.label} ✓`
        hint.className = 'text-sm sm:text-xs h-4 text-green-600'
      } else {
        hint.textContent = `${info.label} — невірна контрольна сума`
        hint.className = 'text-sm sm:text-xs h-4 text-red-500'
      }
    } else {
      hint.textContent = 'ЄДРПОУ = 8 цифр, РНОКПП = 10 цифр'
      hint.className = 'text-sm sm:text-xs h-4 text-gray-400'
    }
  }

  handleResultsUa({ results }) {
    this.trySetValue(`${this.prefixValue}_name`, results.name)
    this.trySetValue(`${this.prefixValue}_tax_no`, results.tax_no)
    this.trySetValue(`${this.prefixValue}_street`, results.street)
    this.trySetValue(`${this.prefixValue}_post_code`, results.post_code)
    this.trySetValue(`${this.prefixValue}_city`, results.city)
    this.trySetValue(`${this.prefixValue}_country`, "UA")
    this.trySetValue(`${this.prefixValue}_phone`, results.phone)

    if (this.prefixValue === "client") {
      this.trySetValue(`${this.prefixValue}_register_number`, results.register_number)
    }
  }
}
